import _extends from "@swc/helpers/src/_extends.mjs";
import React, { useRef, useEffect, useContext } from 'react';
import classnames from 'classnames';
import omitBy from 'lodash/omitBy';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import CheckIcon from '@wix/da-ds/pkg/Icons/Check';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import StandardMenuItemLabel from '../StandardMenuItemLabel';
import StandardMenuSubmenu from '../StandardMenuSubmenu';
import StandardMenuContext from '../context';
import s from './StandardMenuOption.scss';
/**
 * A parent of StandardMenuOption could be a multitude of roles. Each parent role may require a certain role be applied to this component.
 *
 * This returns the correct role for the supplied `parentRole`.
 */ function getRoleBasedOnParent(// The `string | undefined` part here is to get around some typing issues
parentRole) {
    switch(parentRole){
        case 'listbox':
            return 'option';
        case 'menu':
            return 'menuitem';
        default:
            break;
    }
}
const StandardMenuOption = ({ id , item , item: { icon: itemIcon , iconClassName , onClick , onMouseEnter , disabled , danger , className: itemClassName , value , highlighted , selected , label , biData , dataHook , tabIndex , link , target , title , submenu , subMenuPreferredSide , estimatedSubmenuWidth , minimumHeightNeededForSubmenu  } , hideCheckIcon , withHoverEffect , className , focused  })=>{
    const { role: parentRole  } = useContext(StandardMenuContext);
    let icon = null;
    if (itemIcon) {
        // ick
        icon = /*#__PURE__*/ React.cloneElement(itemIcon, {
            className: classnames(s['menu-item-icon'], iconClassName)
        });
    }
    const itemRef = useRef(null);
    useEffect(()=>{
        if (itemRef.current && focused) {
            itemRef.current.focus({
                preventScroll: true
            });
        }
    }, [
        itemRef,
        focused
    ]);
    const content = /*#__PURE__*/ React.createElement(React.Fragment, null, icon, /*#__PURE__*/ React.createElement(StandardMenuItemLabel, {
        item: item
    }), selected && !hideCheckIcon && /*#__PURE__*/ React.createElement(CheckIcon, {
        className: s['selected-icon'],
        size: IconSize.TINY
    }));
    // omitBy to prevent undefined values from overwriting props that we need.
    const sharedProps = omitBy({
        id,
        innerRef: (node)=>{
            itemRef.current = node;
        },
        className: classnames(s['menu-item'], onClick && s['clickable'], disabled && s['disabled'], danger && s['danger'], withHoverEffect && s['with-hover-effect'], highlighted && s['highlighted'], itemClassName, className),
        value,
        disabled,
        selected,
        label: typeof label === 'string' ? label : undefined,
        onClick,
        onMouseEnter,
        biData: biData,
        // We don't want to tab into items individually, the wrapping StandardMenuContent will manage
        // focus itself.
        tabIndex: tabIndex === undefined ? -1 : tabIndex,
        children: content,
        title,
        role: getRoleBasedOnParent(parentRole),
        'data-hook': dataHook,
        // Undefined on false to avoid some extra text being read out in VoiceOver + Chrome
        'aria-selected': selected ? true : undefined,
        'aria-disabled': disabled,
        'data-selected': selected ? 'selected' : undefined
    }, (keyValue)=>keyValue === undefined);
    if (link) {
        return /*#__PURE__*/ React.createElement(BiLink, _extends({
            href: link,
            target: target
        }, sharedProps));
    } else if (submenu) {
        // A11Y TODO: Figure out what role these should have
        // Should likely be the usual `role="getRoleBasedOnParent(...)"` with an `aria-haspopup`
        // https://www.digitala11y.com/option-role/
        return /*#__PURE__*/ React.createElement(StandardMenuSubmenu, _extends({
            submenu: submenu,
            preferredSide: subMenuPreferredSide,
            estimatedSubmenuWidth: estimatedSubmenuWidth,
            minimumHeightNeeded: minimumHeightNeededForSubmenu
        }, sharedProps));
    } else {
        return /*#__PURE__*/ React.createElement(PlainButtonOrLink, _extends({
            isFakeButton: true
        }, sharedProps));
    }
};
StandardMenuOption.displayName = 'StandardMenuOption';
export default StandardMenuOption;
